import { useCurrentFrame, useVideoConfig } from "remotion";
import { EpicycleChain } from "./components/EpicycleChain";
import { fourierConfig as config } from "./config";
import { getKoiDetailFrame, KOI_DETAIL_START } from "./koiDetailModel";
import { koiArtworkScale, koiArtworkTransform, koiModel, koiSourceCenter } from "./koiModel";
import { koiTheme } from "./koiTheme";
import { ease, getTrailLayout } from "./timeline";

/** Every interior line grows from a left-panel Fourier chain and its right-panel tip. */
export const KoiDetailDrawing = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const seconds = frame / fps;
  if (seconds < KOI_DETAIL_START) return null;
  const scene = getKoiDetailFrame(seconds);
  const timing = koiTheme.timing;
  const outroSeconds = seconds - timing.stages.reduce((sum, duration) => sum + duration, 0);
  const focus = ease((outroSeconds - timing.focusDelay) / timing.focusDuration);
  const target = getTrailLayout(frame, fps, timing);
  target.scale += (440 - config.layout.finish.scale) * focus;
  target.x -= koiModel.boundsCenter.x * target.scale * focus;
  target.y -= koiModel.boundsCenter.y * target.scale * focus;
  const screenUnit = target.scale * koiArtworkScale;
  const penExit = 1 - ease((seconds - 11.22) / 0.23);
  return (
    <svg width="100%" height="100%" viewBox="0 0 1080 1920" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <g transform={`translate(${target.x} ${target.y}) scale(${target.scale})`}>
        <g transform={koiArtworkTransform} fill="none" strokeLinecap="round" strokeLinejoin="round">
          {scene.complete.map((stroke) => (
            <path
              key={stroke.id}
              d={stroke.fullPath}
              stroke={stroke.group === "patterns" ? "#5794B4" : "#126D8A"}
              strokeWidth={(stroke.id.startsWith("fin-ray") || stroke.id.startsWith("scale-") ? 4.2 : 5.3) / screenUnit}
            />
          ))}
          {scene.active.map((stroke) => (
            <path key={stroke.id} d={stroke.path} stroke={stroke.group === "patterns" ? "#5794B4" : "#126D8A"} strokeWidth={5.3 / screenUnit} />
          ))}
        </g>
      </g>
      {scene.active.map((stroke, index) => {
        const strong = index < 4;
        const opacity = penExit * (strong ? 0.72 : 0.22);
        const leftX = stroke.anchor.x + (stroke.tip.x - stroke.center.x) * stroke.leftScale;
        const leftY = stroke.anchor.y + (stroke.tip.y - stroke.center.y) * stroke.leftScale;
        const rightX = target.x + (stroke.tip.x - koiSourceCenter.x) * screenUnit;
        const rightY = target.y + (stroke.tip.y - koiSourceCenter.y) * screenUnit;
        return (
          <g key={stroke.id} opacity={opacity}>
            <line x1={leftX} y1={leftY} x2={rightX} y2={rightY} stroke="#42A9BE" strokeWidth={strong ? 2 : 1} strokeDasharray="8 9" />
            <g transform={`translate(${stroke.anchor.x} ${stroke.anchor.y}) scale(${stroke.leftScale}) translate(${-stroke.center.x} ${-stroke.center.y})`}>
              <EpicycleChain chain={stroke.chain} scale={stroke.leftScale} minRadius={2.2} colors={{ circle: "#42A9BE", vector: "#1776A7" }} />
            </g>
            <circle cx={leftX} cy={leftY} r={strong ? 4 : 2.8} fill="#08799B" />
            <circle cx={rightX} cy={rightY} r={strong ? 4.8 : 3} fill="#08799B" />
          </g>
        );
      })}
    </svg>
  );
};
